// angular
import { Params } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
// services
import { PwjToastService } from '@app/core/shared/services/app-toast.service';
import { UserService } from '@app/shared/services/user.service';
// types
import {
  IAllProjectsResponse,
  IProject,
} from '../types/project.interface';
import { IProjectQuotePayload, ProjectQuoteMemberType } from '../types/project-quote.interface';
import { normalizeProjectsSort } from '../constants/projects-sort-items';
import {
  resolveProjectQuoteMemberType,
  resolveProjectQuoteMemberTypeFromProfileType,
} from '../utils/project-quote-access.util';
import { normalizePersonDetailResponse } from '@app/+solutions/shared/utils/normalize-person-detail.util';
import type { IPersonDetailResponse } from '@app/core/shared/interfaces/person-detail.interface';

@Injectable()
export class ProjectsService {
  public projectsList = signal<IProject[]>([]);
  public projectsCount = signal<number>(0);
  public isProjectsLoading = signal<boolean>(false);

  public projectDetail = signal<IProject | null>(null);
  public isProjectDetailLoading = signal<boolean>(false);

  private readonly _httpClient = inject(HttpClient);
  private readonly _toast = inject(PwjToastService);
  private readonly _userService = inject(UserService);

  public getAllProjects(queryParams?: Params) {
    this.isProjectsLoading.set(true);

    const omitFromHttp = new Set(['page', 'perPage']);
    const params: Record<string, string | number | boolean> = {};

    for (const key of Object.keys(queryParams ?? {})) {
      if (omitFromHttp.has(key)) continue;
      const value = queryParams?.[key];
      if (value === undefined || value === null || value === '') continue;
      if (key === 'sort' && typeof value === 'string') {
        params[key] = normalizeProjectsSort(value);
        continue;
      }
      params[key] = value as string | number | boolean;
    }

    return this._httpClient
      .get<IAllProjectsResponse>(`main/golden-thread/public/projects`, { params })
      .subscribe({
        next: res => {
          this.projectsList.set(res.data ?? []);
          this.projectsCount.set(res.count ?? res.data?.length ?? 0);
        },
        error: () => this.isProjectsLoading.set(false),
        complete: () => this.isProjectsLoading.set(false),
      });
  }

  public getProjectDetail(projectId: string) {
    this.projectDetail.set(null);
    this.isProjectDetailLoading.set(true);

    return this._httpClient
      .get<IProject & { data?: IProject }>(
        `main/golden-thread/public/projects/${projectId}`
      )
      .subscribe({
        next: res => {
          const detail = (res as { data?: IProject }).data ?? res;
          this.projectDetail.set(detail);
        },
        error: () => this.isProjectDetailLoading.set(false),
        complete: () => this.isProjectDetailLoading.set(false),
      });
  }

  public resolveCurrentQuoteMemberType$(): Observable<ProjectQuoteMemberType | null> {
    const user = this._userService.userSignal();
    const fromUser = resolveProjectQuoteMemberType(user);
    if (fromUser) {
      return of(fromUser);
    }

    const profileId = user?.profile_id;
    if (!profileId) {
      return of(null);
    }

    return this._httpClient
      .get<IPersonDetailResponse & { data?: IPersonDetailResponse }>(
        `main/people/${profileId}`
      )
      .pipe(
        map(res => {
          const detail = normalizePersonDetailResponse(res ?? null);
          const profileType =
            detail?.job_information?.type ??
            detail?.personal_information?.type;
          return resolveProjectQuoteMemberTypeFromProfileType(profileType);
        }),
        catchError(() => of(null))
      );
  }

  public submitProjectQuote(projectId: string, data: IProjectQuotePayload) {
    return this._httpClient
      .post<IProjectQuotePayload>(
        `main/golden-thread/quotes/project/${projectId}`,
        data
      )
      .subscribe({
        next: () => {
          const current = this.projectDetail();
          if (current?._id === projectId) {
            this.projectDetail.set({
              ...current,
              already_sent_request: true,
            });
          }
          this._toast.success(
            'toastMessages.submitProjectQuote',
            'toastMessages.successTitle'
          );
        },
        error: error => {
          if (error.status === 401) {
            this._toast.error(
              'toastMessages.loginMessage',
              'toastMessages.loginTitle'
            );
          } else {
            this._toast.error('toastMessages.error', 'toastMessages.errorTitle');
          }
        },
      });
  }
}
